/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderObject from "App/geometry/objects/object";
import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import {mat4} from "gl-matrix";
import {generate3DLine, getBytesPerElementFromType} from "App/utils/utils";
import Binary from "App/geometry/objects_geometry/binary";
import Factory from "App/geometry/objects_geometry/factory";

export default
class LineBinary extends AbstractGeometry {

    public static TypeName: string = "lineBinaryGeometry";

    public normalsDesc: VertexDataDescriptor = null;
    public indicesDesc: VertexDataDescriptor = null;
    public distancesDesc: VertexDataDescriptor = null;
    public nonIndexedPositionsDesc: VertexDataDescriptor = null;

    constructor(aObject: RenderObject) {
        super(aObject);
    }

    public static create(aObject: RenderObject) {
        return new LineBinary(aObject);
    }

    public clear() {
        super.clear();

        this.normalsDesc = null;
        this.indicesDesc = null;
        this.distancesDesc = null;
        this.nonIndexedPositionsDesc = null;
    }

    public loadData(aBuffer: Uint8Array) {
        let data: any = {
            positions: null,
            indices: null
        };

        let bpe = getBytesPerElementFromType("Float32");

        let offset = this.properties.offset;
        data.positions = new Float32Array(Binary.getSubBuffer(aBuffer, offset, offset + this.properties.vertexCount * 3 * bpe));
        offset += this.properties.vertexCount * 3 * bpe;

        data.indices = new Uint32Array(Binary.getSubBuffer(aBuffer, offset, offset + this.properties.indexCount * 1 * 4));

        // transform according to repetition
        let repetitionTrafo = this.object.getElement().getRepetitionTransformation();
        if (repetitionTrafo) {
            data.positions = AbstractGeometry.transformArray(
                data.positions,
                this.properties.vertexCount,
                new Float32Array(repetitionTrafo)
            );
        }

        let generatedData: any = {
            positions: [],
            normals: [],
            indices: [],
            distances: []
        };

        generate3DLine(data, generatedData);

        generatedData.positions = new Float32Array(generatedData.positions);
        generatedData.normals = new Float32Array(generatedData.normals);
        generatedData.distances = new Float32Array(generatedData.distances);
        generatedData.indices = new Uint32Array(generatedData.indices);

        this.setDescriptors(generatedData);

        generatedData = null;
        data = null;
    }

    protected setDescriptors(aData: any) {
        // fill mesh with data
        let positionAttributeSize = this.object.getPositionAttributeSize();
        this.vertexCount = new Float32Array(aData.positions).byteLength / (positionAttributeSize * 4);

        this.positionsDesc = new VertexDataDescriptor(
            "positions",
            positionAttributeSize,
            aData.positions.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            aData.positions
        );

        this.normalsDesc = new VertexDataDescriptor(
            "normals",
            3,
            aData.normals.length / 3,
            "FLOAT",
            4,
            0,
            aData.normals
        );

        this.distancesDesc = new VertexDataDescriptor(
            "distances",
            1,
            aData.distances.length,
            "FLOAT",
            4,
            0,
            aData.distances
        );

        this.indicesDesc = new VertexDataDescriptor(
            "indices",
            1,
            aData.indices.length,
            "UINT",
            4,
            0,
            aData.indices
        );

        this.numIndices = aData.indices.length;
        this.numFaces = aData.indices.length / this.object.getPrimitiveSize();

        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );
    }

    public transform(aTransformation: mat4) {
        AbstractGeometry.prototype.transform.call(this, aTransformation);

        let positionAttributeSize = this.object.getPositionAttributeSize();
        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );
    }
}

Factory.register(LineBinary);
